import {useState, ChangeEvent, Dispatch, SetStateAction} from 'react'
import {useDebouncedCallback} from 'use-debounce'
import {Input} from 'antd'

import styles from './DocumentsSearch.module.scss'
import {Block, Text} from 'common/components'
import {
  Document,
  useProjectUuid,
  useGetDocuments,
  useGetChatFiles,
  useDrawdownsTabs,
  useAMLTabs,
  DOCUMENTS_CATEGORY,
  DOCUMENTS_COLLECTION,
  IDocument,
  IDocumentsTab,
} from '..'
import { RequestTagType } from 'modules/request'

interface DocumentsSearchProps {
  isRequest?: boolean
  tag?: RequestTagType
}

interface SearchInputProps {
  search: string
  setSearch: Dispatch<SetStateAction<string>>
}

interface ResultProps {
  projectUuid: string
  search: string
  documents: IDocument[]
  drawdownsTabs: IDocumentsTab[]
  amlTabs: IDocumentsTab[]
}

export default function DocumentsSearch({isRequest, tag}: DocumentsSearchProps) {
  const projectUuid = useProjectUuid(isRequest, tag)
  const {data: documents} = useGetDocuments(projectUuid)
  const {drawdownsTabs} = useDrawdownsTabs(documents, isRequest)
  const {amlTabs, amlDocuments} = useAMLTabs(projectUuid)
  const {data: chatFiles} = useGetChatFiles(projectUuid)
  const [search, setSearch] = useState('')

  const trimmedSearch = search.trim()

  const activeDocuments = trimmedSearch
    ? [...documents, ...chatFiles, ...amlDocuments].filter(
        (document) =>
          !document.archived &&
          Boolean(DOCUMENTS_COLLECTION[document.collection])
      )
    : []

  return (
    <Block className={styles.root}>
      <div className={styles.panel}>
        <Text as="h3" weight="medium" size="3xl">
          Search
        </Text>
        <SearchInput search={search} setSearch={setSearch} />
      </div>
      <Result
        projectUuid={projectUuid}
        search={trimmedSearch}
        documents={activeDocuments}
        drawdownsTabs={drawdownsTabs}
        amlTabs={amlTabs}
      />
    </Block>
  )
}

const SearchInput = ({search, setSearch}: SearchInputProps) => {
  const [value, setValue] = useState(search)

  const debouncedSetSearch = useDebouncedCallback(
    (value) => setSearch(value),
    300
  )

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    debouncedSetSearch(e.target.value)
  }

  return (
    <Input
      autoFocus
      allowClear
      value={value}
      type="search"
      placeholder="Seacrh across documents…"
      onChange={handleSearchChange}
    />
  )
}

const Result = ({
  projectUuid,
  search,
  documents,
  drawdownsTabs,
  amlTabs,
}: ResultProps) => {
  if (!search) return null

  const resultDocuments = documents.filter((document) =>
    document.name.toLowerCase().includes(search.toLowerCase())
  )

  if (!resultDocuments.length) return null

  resultDocuments.sort(
    (a, b) =>
      getDocumentCollection(a).categoryOrder -
      getDocumentCollection(b).categoryOrder
  )
  resultDocuments.sort(
    (a, b) => getDocumentCategory(a).order - getDocumentCategory(b).order
  )

  return (
    <div className={styles.result}>
      <ul className={styles.list}>
        {resultDocuments.map((document) => (
          <li key={document.uuid}>
            <Document
              searchPage
              projectUuid={projectUuid}
              search={search}
              document={document}
              drawdownsTabs={drawdownsTabs}
              amlTabs={amlTabs}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

const getDocumentCollection = (document: IDocument) =>
  DOCUMENTS_COLLECTION[document.collection]

const getDocumentCategory = (document: IDocument) =>
  DOCUMENTS_CATEGORY[getDocumentCollection(document).categoryKey]
